import React from 'react';
import { useDispatch } from 'react-redux';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import emailSenderNamesResource from 'src/actions/email/emailSenderNamesResource';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import { STATUS_PERSISTING, useResourceValues } from 'src/presentation/hooks/useResource';

const dataAidPrefix = 'admin-email-address-';

const FormDescription = ({ email }) => (
  <div>
    Modify your email configuration for {email}. Review{' '}
    <ExternalLink
      data-aid="manage-email-help-link"
      label="how to manage email"
      to="https://help.gladly.com/implementation/docs/set-up-email"
    />
    {'. '}Email usernames and sending domains cannot be modified.
  </div>
);

export default function EmailAddressEditor({ errors, item, root }: FormEditorProps) {
  const dispatch = useDispatch();

  const { persistingState: emailSenderNamesPersistingState } = useResourceValues(emailSenderNamesResource);

  const isPersisting = emailSenderNamesPersistingState === STATUS_PERSISTING;

  const formContext = useForm({
    errors,
    item: item,
    resetErrors: emailSenderNamesResource.actions.resetErrors,
    root,
    onSave: ({ workingState }) => {
      dispatch(emailSenderNamesResource.effects.updateSenderName(workingState));
    },
  });

  const { onClose, onSave } = formContext;

  return (
    <Form context={formContext}>
      <H1>Edit email configuration</H1>

      <Body data-aid={`${dataAidPrefix}editor`}>
        <Section description={<FormDescription email={item.address} />}>
          <Row>
            <Input
              data-aid={`${dataAidPrefix}editor-sender-name`}
              attr="senderName"
              autoFocus
              label="Email sender name"
              placeholder="Sender name"
              description="The name email recipients see (e.g. “Gladly” at support@gladly.com)"
            />
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-save`} onClick={onSave} isLoading={isPersisting}>
          Save
        </Button>
      </Footer>
    </Form>
  );
}
